/**
 * Letterhead printing — for issuing official letters on company stationery.
 *
 * Architecture (true page-margin letterhead, per company-stationery spec):
 *   ┌────────────────────────────────────────────┐
 *   │ HEADER  (logo + brand + contact + QR)      │  ← fixed top region
 *   ├────────────────────────────────────────────┤
 *   │                                            │
 *   │            DOCUMENT BODY                   │  ← flowing content
 *   │                                            │
 *   ├────────────────────────────────────────────┤
 *   │ LEGAL FOOTER  (RCS / TVA / IBAN ...)       │  ← fixed bottom region
 *   └────────────────────────────────────────────┘
 *
 * The header and footer are placed in the PAGE MARGIN areas via
 * `position: fixed; top:0` and `position: fixed; bottom:0`. Reserved space is
 * provided by `body { padding-top / padding-bottom }`. Body content therefore
 * never overlaps either band, and Chromium repeats the fixed bands on every
 * printed page automatically — exactly like printing on pre-printed stationery.
 */

import { BRAND_ASSETS } from '../config/brandAssets';
import { openPrintWindow } from './openPrintWindow';

const LOGO_URL = BRAND_ASSETS.LOGO_PRIMARY;

// Same custom QR artwork used on the invoice. Change to swap destination.
const QR_CODE_URL = BRAND_ASSETS.REVIEW_QR;

const COMPANY = {
    facebook: '/UniCosm',
    instagram: '@UniCosm',
    phone: '+33 (0) 1 47 43 28 12',
    email: 'bonjour@universcosmetic.eu',
    website: 'www.universcosmetic.eu',
    tagline: 'PARTENAIRE DE CONFIANCE EN DISTRIBUTION BEAUTÉ',
    legal_line_1: 'SAS UNIVERS COSMETIC, 21 RUE EUGENE HENAFF, 93000 BOBIGNY, FRANCE.',
    legal_line_2:
        'SIEGE SOCIAL : 89302923100014 RCS BOBIGNY, TVA INTRACOM : FR85893029231, EORI N° FR89302923100014, APE : 4775Z',
    legal_line_3: 'IBAN : FR76 3000 3028 3900 0200 3387 119, BIC - SWIFT : SOGEFRPP',
};

// Reserved page-margin sizes — also drive the per-page spacers (table
// <thead>/<tfoot>) that push body content out of the fixed header/footer
// bands on every printed page.
const HEADER_HEIGHT_MM = 38;   // logo row + contact strip + divider
const FOOTER_HEIGHT_MM = 22;   // 3 legal lines + divider
const PAGE_SIDE_MM = 16;       // left/right page padding for body content

const ICON = {
    facebook: '<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.6 9.9V14.9H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12Z"/></svg>',
    instagram: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>',
    phone: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.7 2Z"/></svg>',
    email: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>',
};

/**
 * Build the printable HTML for a letterhead document.
 *
 * @param {object} opts
 * @param {string} opts.bodyHtml   Rich-text body (from the Tiptap editor).
 * @param {boolean} [opts.showQr]  Defaults to true. QR appears in every page's header.
 */
export function buildLetterHtml(opts) {
    const bodyHtml = opts.bodyHtml || '';
    const showQr = opts.showQr !== false;

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>Lettre — UNIVERS COSMETIC</title>
<style>
  /* TRUE PAGE-MARGIN LETTERHEAD — clean cross-browser pattern.
     @page has zero margin (no compounding with Chrome's print-dialog
     defaults). The header is position:fixed;top:0 — it sits at the page
     edge. The legal footer is position:fixed;bottom:0 — at the bottom
     edge. To stop body content from rendering UNDER the bands, we use a
     transparent table-header-group + table-footer-group spacer that
     Chromium auto-repeats on every printed page. */
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: 'Century Gothic','Manrope','DM Sans',Arial,sans-serif;
    color: #1A1A1A;
    font-size: 12px;
    line-height: 1.55;
  }

  /* Per-page spacer table — repeats <thead>/<tfoot> automatically. */
  table.page-frame {
    width: 100%;
    border-collapse: collapse;
    padding: 0 ${PAGE_SIDE_MM}mm;
  }
  table.page-frame > thead { display: table-header-group; }
  table.page-frame > tfoot { display: table-footer-group; }
  table.page-frame > thead > tr > td.spacer {
    height: ${HEADER_HEIGHT_MM}mm;
    padding: 0; border: 0; background: transparent;
  }
  table.page-frame > tfoot > tr > td.spacer {
    height: ${FOOTER_HEIGHT_MM}mm;
    padding: 0; border: 0; background: transparent;
  }
  table.page-frame > tbody > tr > td {
    padding: 4mm ${PAGE_SIDE_MM}mm 4mm ${PAGE_SIDE_MM}mm;
    border: 0;
  }

  /* ============ FIXED PAGE-MARGIN HEADER (repeats on every page) ============ */
  .page-header {
    position: fixed;
    top: 0; left: 0; right: 0;
    height: ${HEADER_HEIGHT_MM}mm;
    padding: 10mm ${PAGE_SIDE_MM}mm 0 ${PAGE_SIDE_MM}mm;
    background: #fff;
    z-index: 10;
  }
  .page-header .hdr-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding: 0 0 8px 0;
    border-bottom: 1.5px solid #1A1A1A;
  }
  .page-header .brand { display: flex; align-items: center; gap: 14px; }
  .page-header .brand img { width: 56px; height: 56px; object-fit: contain; }
  .page-header .brand h1 {
    margin: 0;
    font-size: 19px;
    font-weight: 600;
    letter-spacing: 2px;
    line-height: 1;
  }
  .page-header .brand h1 span { font-weight: 300; letter-spacing: 4px; }
  .page-header .brand-text { display: inline-block; }
  .page-header .brand .tagline {
    color: #888;
    font-size: 7.5px;
    letter-spacing: 0.6px;
    margin-top: 6px;
    text-transform: uppercase;
    max-width: 230px;
    line-height: 1.45;
  }
  .page-header .qr-corner { width: 62px; height: 62px; }
  .page-header .qr-corner img { width: 62px; height: 62px; object-fit: contain; }

  .page-header .info-strip {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 18px;
    padding: 7px 0 0 0;
    color: #555;
    font-size: 9.5px;
  }
  .page-header .info-strip .group { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .page-header .info-strip .item { display: inline-flex; align-items: center; gap: 5px; color: #555; white-space: nowrap; }
  .page-header .info-strip .item svg { color: #C08478; flex: 0 0 auto; }

  /* ============ LETTER BODY ============ */
  .letter-body {
    font-size: 12px;
    line-height: 1.7;
    color: #1A1A1A;
  }
  .letter-body p { margin: 0 0 12px 0; }
  .letter-body h1, .letter-body h2, .letter-body h3 {
    color: #1A1A1A;
    margin: 18px 0 10px 0;
  }
  .letter-body h1 { font-size: 18px; letter-spacing: 1px; }
  .letter-body h2 { font-size: 15px; }
  .letter-body h3 { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #C08478; }
  .letter-body ul, .letter-body ol { margin: 0 0 12px 22px; }
  .letter-body li { margin-bottom: 4px; }
  .letter-body blockquote {
    margin: 12px 0;
    padding: 6px 14px;
    border-left: 3px solid #C08478;
    color: #555;
    font-style: italic;
  }
  .letter-body a { color: #C08478; text-decoration: underline; }
  .letter-body strong { color: #1A1A1A; }
  .letter-body em { font-style: italic; }

  /* ============ FIXED PAGE-MARGIN LEGAL FOOTER (repeats on every page) ============ */
  .legal-footer {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    height: ${FOOTER_HEIGHT_MM}mm;
    padding: 6mm ${PAGE_SIDE_MM}mm 4mm ${PAGE_SIDE_MM}mm;
    border-top: 1.5px solid #1A1A1A;
    background: #fff;
    font-size: 8.5px;
    line-height: 1.55;
    color: #444;
    text-align: center;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    z-index: 10;
  }
  .legal-footer .l1 { font-weight: 700; color: #1A1A1A; }
  .legal-footer .l2, .legal-footer .l3 { color: #555; }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body onload="window.print()">

<!-- HEADER — placed in the top page margin via position:fixed -->
<header class="page-header">
  <div class="hdr-top">
    <div class="brand">
      <img src="${LOGO_URL}" alt="UNIVERS COSMETIC" />
      <div class="brand-text">
        <h1>UNIVERS <span>COSMETIC</span></h1>
        <div class="tagline">${COMPANY.tagline}</div>
      </div>
    </div>
    ${showQr ? `<div class="qr-corner"><img src="${QR_CODE_URL}" alt="Google Review QR" /></div>` : ''}
  </div>
  <div class="info-strip">
    <div class="group">
      <span class="item">${ICON.facebook}<span>${COMPANY.facebook}</span></span>
      <span class="item">${ICON.instagram}<span>${COMPANY.instagram}</span></span>
    </div>
    <div class="group">
      <span class="item">${ICON.phone}<span>${COMPANY.phone}</span></span>
      <span class="item">${ICON.email}<span>${COMPANY.email}</span></span>
    </div>
  </div>
</header>

<!-- BODY: wrapped in a page-frame table so the <thead>/<tfoot> spacer
     rows auto-repeat on every printed page, keeping the body content
     out of the fixed header/footer bands. -->
<table class="page-frame">
  <thead><tr><td class="spacer"></td></tr></thead>
  <tfoot><tr><td class="spacer"></td></tr></tfoot>
  <tbody><tr><td>
    <main class="letter-body">${bodyHtml}</main>
  </td></tr></tbody>
</table>

<!-- LEGAL FOOTER — placed in the bottom page margin via position:fixed -->
<footer class="legal-footer">
  <div class="l1">${COMPANY.legal_line_1}</div>
  <div class="l2">${COMPANY.legal_line_2}</div>
  <div class="l3">${COMPANY.legal_line_3}</div>
</footer>

</body>
</html>`;
}

export function printLetter(opts) {
    const html = buildLetterHtml(opts);
    const w = openPrintWindow(html, { width: 1000, height: 1100 });
    if (!w) return;
}
